"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CircleCheck, Sprout, Users, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinTheGrowerCircle() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="120303" id="batch-update-subscription" className="dark bg-[#0a0a0a] text-white py-16 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-30 mix-blend-screen"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl opacity-20 mix-blend-screen"></div>
      </div>

      <div ref={ref} className={`relative z-10 max-w-4xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-none mb-6 border border-primary/20">
            <Sprout className="size-8 text-primary" />
          </div>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tighter mb-4 font-sans uppercase">
            Receive Batch Purity Reports
          </h2>
          <p className="text-base text-slate-400 max-w-2xl mx-auto flex items-center justify-center gap-2 font-light">
            <Users className="size-5 text-primary" />
            Join 5,000+ researchers receiving validated analytical data and batch updates.
          </p>
        </div>

        <div className="bg-[#111111] backdrop-blur-md border border-white/5 rounded-none p-4 md:p-6 shadow-2xl">
          <FormProvider formKey="batch_update_subscription_home" formId="batch_update_subscription_home" sectionName="Batch Update Subscription" defaultValues={{
          first_name: "",
          email: ""
        }} validationRules={{
          first_name: {
            required: "First Name is required"
          },
          email: {
            required: "Institutional email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }
        }} submitText="Subscribe to Laboratory Updates" successMessage="Subscription confirmed. Your 10% discount code has been sent to your institutional inbox.">
            <GrowerCircleForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function GrowerCircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-12 text-center flex flex-col items-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-primary/10 rounded-none border border-primary/30 flex items-center justify-center mb-2">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <h3 className="text-2xl font-medium text-white uppercase tracking-wider">Subscription Confirmed</h3>
        <p className="text-slate-400 max-w-md text-lg font-light">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-none border-white/20 hover:bg-white/5 px-8 uppercase tracking-widest text-xs">
          Register Secondary Terminal
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-slate-500 font-medium">First Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Julian" {...field} className="bg-transparent border-0 border-b border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
              </FormControl>
              <FormDescription className="text-[10px] uppercase text-slate-600">Enter your name for verification</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-slate-500 font-medium">Institutional Email <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="researcher@signalsource.one" {...field} className="bg-transparent border-0 border-b border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-primary/10 border border-primary/20 rounded-none text-primary text-xs uppercase tracking-widest flex items-center gap-2">
          <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-medium uppercase tracking-[0.2em] rounded-none shadow-none bg-primary hover:bg-primary/90 text-white transition-all group">
        {isSubmitting ? <span className="flex items-center gap-2">
            <Loader className="animate-spin size-4" />
            Authenticating...
          </span> : <span className="flex items-center gap-2">
            Subscribe to Reports
            <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
          </span>}
      </Button>
    </Form>;
}